import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  TablePagination,
  Box,
  List,
  ListItem,
  ListItemText,
  Grid,
  ListItemSecondaryAction,
  Typography,
  Stack
} from '@mui/material';
import ReportIcon from '@mui/icons-material/Report';
import { statusMessages } from 'types/types';
import { convertDateTime } from 'utils/date-time';

const pageSize = {
  ROWSPERPAGE: 5,
  SETPAGE: 0
};

const useStyles = makeStyles((theme) => ({
  tablePagination: {
    borderBottom: theme.spacing(0),
    display: 'flex',
    marginLeft: '-8px'
  },
  secondary: {
    color: theme.palette.error.main,
    paddingRight: theme.spacing(0.4)
  },
  success: {
    backgroundColor: theme.palette.success.light
  },
  executing: {
    backgroundColor: theme.palette.warning.main
  },
  lightError: {
    backgroundColor: theme.palette.error.light
  },
  failed: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: '33%',
    alignItems: 'center'
  },
  reportIcon: {
    color: theme.palette.error.main,
    paddingLeft: theme.spacing(0.625)
  },
  textBold: {
    fontWeight: theme.typography.fontWeightMedium
  },
  primaryText: {
    overflowWrap: 'break-word',
    maxWidth: '80%'
  }
}));

type DagsHistoryTableProps = {
  dagrunData: HistoryProps[];
};

interface HistoryProps {
  dagrunId: string;
  ecid: string;
  dagrunStatus: string;
  dagrunStartedAt: string;
  errorMessage: string;
  message: string;
}

export const DagsHistoryListItems: React.FC<DagsHistoryTableProps> = ({
  dagrunData
}: DagsHistoryTableProps) => {
  const classes = useStyles();
  const [rowsPerPage, setRowsPerPage] = useState(pageSize.ROWSPERPAGE);
  const [page, setPage] = useState(pageSize.SETPAGE);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rowsToRender =
    rowsPerPage > 0
      ? dagrunData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : dagrunData;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <List dense={true}>
            {rowsToRender.map((row) => (
              <ListItem
                key={row.dagrunStartedAt}
                dense={true}
                secondaryAction={
                  <ListItemSecondaryAction>
                    {statusMessages.includes(row.dagrunStatus) ? (
                      <Typography
                        variant="body2"
                        className={
                          row.dagrunStatus == 'success'
                            ? classes.success
                            : classes.executing
                        }
                      >
                        {row.dagrunStatus[0].toUpperCase() +
                          row.dagrunStatus.slice(1)}
                      </Typography>
                    ) : (
                      <Stack className={classes.failed}>
                        <Typography
                          variant="body2"
                          className={classes.lightError}
                        >
                          {row.dagrunStatus[0].toUpperCase() +
                            row.dagrunStatus.slice(1)}
                        </Typography>
                        <ReportIcon
                          className={classes.reportIcon}
                          style={{
                            marginTop: '0px'
                          }}
                        />
                      </Stack>
                    )}
                  </ListItemSecondaryAction>
                }
              >
                <ListItemText
                  primary={
                    <span className={classes.textBold}>
                      {convertDateTime(row.dagrunStartedAt)}
                    </span>
                  }
                  secondary={
                    row.dagrunStatus === 'success' ? (
                      `${row.message || 'Processed'} | JobId: ${row.ecid}`
                    ) : row.dagrunStatus === 'executing' ? (
                      `JobId: ${row.ecid}`
                    ) : (
                      <>
                        <span className={classes.secondary}>
                          {row.errorMessage}
                        </span>
                        | JobId: {row.ecid}
                      </>
                    )
                  }
                  className={classes.primaryText}
                />
              </ListItem>
            ))}
            {dagrunData.length > 1 ? (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                colSpan={5}
                count={dagrunData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{
                  selectLabel: classes.textBold,
                  displayedRows: classes.textBold,
                  select: classes.textBold,
                  menuItem: classes.textBold
                }}
                className={classes.tablePagination}
              />
            ) : null}
          </List>
        </Grid>
      </Grid>
    </Box>
  );
};
